import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  CheckCircle, Prohibit, ShieldCheck, X, ArrowCounterClockwise,
  SquaresFour, UserPlus, Trash, MagnifyingGlass, Key, Copy, Database,
} from "@phosphor-icons/react";
import { OracleIdentityPanel } from "@/components/erp/OracleIdentityPanel";

const ROLE_COLOR = {
  product_admin: "border-[#0020C2] bg-blue-50 text-[#0020C2]",
  admin: "border-purple-300 bg-purple-50 text-purple-700",
  maker: "border-amber-300 bg-amber-50 text-amber-700",
  checker: "border-teal-300 bg-teal-50 text-teal-700",
  user: "border-gray-300 bg-gray-50 text-gray-700",
};
const ROLES = ["product_admin", "admin", "maker", "checker", "user"];

// ----- Inline role picker -----------------------------------------------------
const RolePicker = ({ user, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const change = async (e) => {
    const next = e.target.value;
    if (next === user.role) return;
    setBusy(true);
    try {
      await api.put(`/erp-agents/admin/users/${user.id}/role`, { role: next });
      await onChanged();
    } finally { setBusy(false); }
  };
  return (
    <select
      value={user.role}
      disabled={busy}
      onChange={change}
      data-testid={`user-role-${user.id}`}
      className={`text-[10px] font-bold tracking-wider uppercase border px-2 py-1 bg-white cursor-pointer ${ROLE_COLOR[user.role] || ROLE_COLOR.user}`}
    >
      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  );
};

// ----- Reset-password modal ---------------------------------------------------
const ResetPasswordModal = ({ user, onClose }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const generate = () => {
    // Generate a 14-char random password with mixed alphabet + digits + symbol
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const sym = "!@#$%&*";
    let out = "";
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    out += sym[Math.floor(Math.random() * sym.length)];
    out += Math.floor(Math.random() * 90 + 10);
    setPassword(out);
    setConfirm(out);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.put(`/erp-agents/admin/users/${user.id}/password`, { password });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(password); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white text-gray-900 max-w-md w-full border border-gray-300"
        data-testid="reset-password-modal">
        <div className="p-5 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
              <Key size={12} weight="fill" /> Reset Password
            </div>
            <h3 className="text-xl font-black tracking-tight mt-1">{user.name || user.email}</h3>
            <div className="text-[11px] text-gray-500 font-mono mt-0.5">{user.email}</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-5 space-y-3" data-testid="reset-password-done">
            <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm">
              <CheckCircle size={18} weight="fill" /> Password updated.
            </div>
            <div className="text-[12px] text-gray-700">
              Share this with <span className="font-mono">{user.email}</span> over a secure channel — no email is sent.
            </div>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-2">
              <code className="flex-1 text-sm font-mono break-all" data-testid="reset-password-final">{password}</code>
              <button type="button" onClick={copy}
                data-testid="reset-password-copy"
                className="text-gray-500 hover:text-[#0020C2]">
                <Copy size={14} weight="bold" />
              </button>
            </div>
            <div className="pt-2 flex justify-end">
              <button type="button" onClick={onClose}
                data-testid="reset-password-close"
                className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-[#0020C2] text-white">
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">New password</label>
                  <button type="button" onClick={generate}
                    data-testid="reset-password-generate"
                    className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#0020C2] hover:underline">
                    Generate strong
                  </button>
                </div>
                <input
                  required type="text" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0020C2]"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Confirm password</label>
                <input
                  required type="text" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0020C2]"
                />
              </div>
              {err && <div className="text-[12px] text-[#DC2626] font-semibold" data-testid="reset-password-error">{err}</div>}
              <div className="text-[11px] text-gray-500">
                Minimum 6 characters. The user will need to log out and back in.
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-2">
              <button type="button" onClick={onClose}
                className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider border border-gray-300">
                Cancel
              </button>
              <button type="submit" disabled={busy}
                data-testid="reset-password-submit"
                className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-[#0020C2] text-white disabled:opacity-60">
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

// ----- Invite-user modal ------------------------------------------------------
const InviteModal = ({ onClose, onInvited }) => {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user", company: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/erp-agents/admin/users", form);
      await onInvited();
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white text-gray-900 max-w-md w-full border border-gray-300"
        data-testid="invite-user-modal">
        <div className="p-5 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
              <UserPlus size={12} weight="fill" /> Invite User
            </div>
            <h3 className="text-xl font-black tracking-tight mt-1">Add new user</h3>
            <div className="text-[11px] text-gray-500 mt-0.5">Share the initial password with them — no email is sent.</div>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {["email", "name", "password", "company"].map((k) => (
            <div key={k}>
              <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">{k}</label>
              <input
                required={k !== "company"}
                type={k === "password" ? "password" : k === "email" ? "email" : "text"}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                data-testid={`invite-${k}`}
                className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0020C2]"
              />
            </div>
          ))}
          <div>
            <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="invite-role"
              className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {err && <div className="text-[12px] text-[#DC2626] font-semibold" data-testid="invite-error">{err}</div>}
        </div>
        <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider border border-gray-300">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="invite-submit"
            className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-[#0020C2] text-white">
            {busy ? "Inviting…" : "Invite user"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ----- Per-user overrides drawer ----------------------------------------------
const UserOverrideDrawer = ({ user, catalog, menuCatalog, onClose, onSaved }) => {
  const overrides = (user.agent_overrides || []).reduce((m, o) => { m[o.agent_key] = o.enabled; return m; }, {});
  const [busy, setBusy] = useState("");
  const [menus, setMenus] = useState(user.allowed_menus || []);
  const [savingMenus, setSavingMenus] = useState(false);

  useEffect(() => { setMenus(user.allowed_menus || []); }, [user]);

  const set = async (agent_key, enabled) => {
    setBusy(agent_key);
    try {
      await api.put(`/erp-agents/admin/users/${user.id}/agent-overrides/${agent_key}`, { enabled });
      await onSaved();
    } finally { setBusy(""); }
  };

  const clear = async (agent_key) => {
    setBusy(agent_key);
    try {
      await api.delete(`/erp-agents/admin/users/${user.id}/agent-overrides/${agent_key}`);
      await onSaved();
    } finally { setBusy(""); }
  };

  const toggleMenu = async (key) => {
    const next = menus.includes(key) ? menus.filter((m) => m !== key) : [...menus, key];
    setMenus(next);
    setSavingMenus(true);
    try {
      await api.put(`/erp-agents/admin/users/${user.id}/menus`, { menus: next });
      await onSaved();
    } finally { setSavingMenus(false); }
  };

  const groups = catalog.reduce((m, a) => { (m[a.effective.category] = m[a.effective.category] || []).push(a); return m; }, {});

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white text-gray-900 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-300"
        data-testid="user-overrides-drawer">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
              <ShieldCheck size={12} weight="fill" /> Per-User Access
            </div>
            <h3 className="text-2xl font-black tracking-tight mt-1">{user.name || user.email}</h3>
            <div className="text-xs text-gray-500 font-mono mt-1">{user.email} · {user.role} · {user.company || "no-company"}</div>
          </div>
          <button onClick={onClose} data-testid="user-overrides-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div data-testid="user-oracle-section">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2 flex items-center gap-1.5">
              <Database size={12} weight="fill" /> Oracle Identity &amp; Data Access
            </div>
            <div className="border border-gray-200 p-4">
              <OracleIdentityPanel userId={user.id} onSaved={onSaved} />
            </div>
          </div>

          <div data-testid="user-menus-section">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2 flex items-center gap-1.5">
              <SquaresFour size={12} weight="fill" /> Main Menu Access
            </div>
            <div className="border border-gray-200 divide-y divide-gray-100">
              {menuCatalog.map((m) => {
                const on = menus.includes(m.key);
                const disabled = m.product_admin_only && user.role !== "product_admin";
                return (
                  <div key={m.key} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50"
                    data-testid={`user-menu-row-${m.key}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{m.label}</div>
                      <div className="text-[10px] font-mono text-gray-500 truncate">
                        {m.key}{m.product_admin_only && " · product_admin only"}
                      </div>
                    </div>
                    <button
                      onClick={() => !disabled && toggleMenu(m.key)}
                      disabled={disabled || savingMenus}
                      data-testid={`user-menu-toggle-${m.key}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wider uppercase border ${
                        disabled
                          ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                          : on
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-red-300 bg-red-50 text-[#DC2626]"
                      }`}>
                      {on ? <><CheckCircle size={10} weight="fill" /> On</> : <><Prohibit size={10} weight="fill" /> Off</>}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-gray-500 mt-1.5">
              Controls which top-level sidebar groups this user sees. Sub-features (ERP agents) are managed below.
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-2 flex items-center gap-1.5">
              <Sparkle /> Per-Agent Overrides
            </div>
            {Object.entries(groups).map(([cat, items]) => (
              <div key={cat} className="mb-4">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 mb-1">{cat.replace("_", " ")}</div>
                <div className="border border-gray-200 divide-y divide-gray-100">
                  {items.map((row) => {
                    const ak = row.agent_key;
                    const override = overrides[ak];
                    const hasOverride = override !== undefined;
                    const effectiveOn = hasOverride ? override : row.effective.enabled;
                    return (
                      <div key={ak} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50" data-testid={`user-agent-row-${ak}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{row.effective.name}</div>
                          <div className="text-[10px] font-mono text-gray-500 truncate">{ak} {!row.effective.enabled && "· tenant-disabled"}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => set(ak, !effectiveOn)} disabled={busy === ak}
                            data-testid={`user-toggle-${ak}`}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold tracking-wider uppercase border ${
                              effectiveOn
                                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                : "border-red-300 bg-red-50 text-[#DC2626]"
                            }`}>
                            {effectiveOn ? <><CheckCircle size={10} weight="fill" /> On</> : <><Prohibit size={10} weight="fill" /> Off</>}
                          </button>
                          {hasOverride && (
                            <button onClick={() => clear(ak)} disabled={busy === ak}
                              data-testid={`user-clear-${ak}`}
                              title="Clear override (use tenant default)"
                              className="text-gray-400 hover:text-[#DC2626]">
                              <ArrowCounterClockwise size={12} weight="bold" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Sparkle = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="text-[#0020C2]"><path d="M8 0l1.6 4.4L14 6 9.6 7.6 8 12 6.4 7.6 2 6l4.4-1.6L8 0z"/></svg>
);

// Compact relative-time formatter ("3d ago", "Just now", "Jan 4").
const formatRelative = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  const diff = Date.now() - then.getTime();
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (diff < min) return "Just now";
  if (diff < hr)  return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function UsersAccessPage() {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [menuCatalog, setMenuCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c, m] = await Promise.all([
        api.get("/erp-agents/admin/users"),
        api.get("/erp-agents/tenant-config"),
        api.get("/erp-agents/menu-catalog"),
      ]);
      setUsers(u.data || []);
      setCatalog(c.data || []);
      setMenuCatalog((m.data && m.data.menus) || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedUser) {
      const fresh = users.find((u) => u.id === selectedUser.id);
      if (fresh && fresh !== selectedUser) setSelectedUser(fresh);
    }
  }, [users, selectedUser]);

  const onDelete = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their Oracle profile and per-user overrides too.`)) return;
    await api.delete(`/erp-agents/admin/users/${u.id}`);
    await load();
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.company || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q))
    : users;

  return (
    <div>
      <PageHeader
        eyebrow="Admin Console"
        title="Users & Access"
        subtitle="Every user across all tenants. Manage role, main-menu visibility, and per-agent overrides from one place."
        testid="users-access-title"
      />
      <div className="p-8">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="users-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, name, company, role…"
              className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#0020C2]"
            />
          </div>
          <div className="text-[11px] text-gray-500" data-testid="users-count">
            {filtered.length} of {users.length} users
          </div>
          <button
            onClick={() => setShowInvite(true)}
            data-testid="invite-user-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-[#0020C2] text-white">
            <UserPlus size={12} weight="bold" /> Invite User
          </button>
        </div>

        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-access-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {[
                  ["Email", "min-w-[200px]"],
                  ["Name", "min-w-[180px]"],
                  ["Role", ""],
                  ["Company", "min-w-[120px]"],
                  ["Created", ""],
                  ["Oracle", ""],
                  ["Main Menus", ""],
                  ["Agent Overrides", ""],
                  ["Actions", ""],
                ].map(([h, cls]) => (
                  <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${cls}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{u.name || "—"}</td>
                  <td className="px-4 py-3"><RolePicker user={u} onChanged={load} /></td>
                  <td className="px-4 py-3 text-gray-700">{u.company || "—"}</td>
                  <td className="px-4 py-3 text-[11px] text-gray-500 whitespace-nowrap" data-testid={`user-created-${u.id}`}>
                    {formatRelative(u.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    {u.oracle_profile_configured ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                        <CheckCircle size={11} weight="fill" /> Set
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-500">
                        <Prohibit size={11} weight="fill" /> Not set
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`user-menus-cell-${u.id}`}>
                    {(u.allowed_menus || []).length}/{menuCatalog.length} <span className="text-gray-400">visible</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {(u.agent_overrides || []).length === 0
                      ? <span className="text-gray-400">none</span>
                      : `${u.agent_overrides.filter((o) => !o.enabled).length} off · ${u.agent_overrides.filter((o) => o.enabled).length} on`}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedUser(u)} data-testid={`user-manage-${u.id}`}
                        className="text-[11px] font-bold uppercase tracking-wider text-[#0020C2] hover:underline">
                        Manage →
                      </button>
                      <button onClick={() => setResetUser(u)} data-testid={`user-reset-password-${u.id}`}
                        title="Reset password"
                        className="text-gray-400 hover:text-[#0020C2]">
                        <Key size={14} weight="bold" />
                      </button>
                      <button onClick={() => onDelete(u)} data-testid={`user-delete-${u.id}`}
                        title="Delete user"
                        className="text-gray-400 hover:text-[#DC2626]">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">
                  {loading ? "Loading…" : (q ? "No users match your search." : "No users yet.")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {selectedUser && (
        <UserOverrideDrawer user={selectedUser} catalog={catalog} menuCatalog={menuCatalog}
          onClose={() => setSelectedUser(null)} onSaved={load} />
      )}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInvited={load} />}
      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
